import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';
import useInvalidate from '../../hooks/useInvalidate';
import './clientes.css';

function formatFecha(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString('es-CO', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return String(value);
  }
}

function esSucursalArchivo(name) {
  return /sucursal/i.test(String(name || ''));
}

function esTerceroArchivo(name) {
  return /tercero/i.test(String(name || ''));
}

export default function ClientesImportPage({ pin, onToast }) {
  const terceroRef = useRef(null);
  const sucursalRef = useRef(null);
  const [tab, setTab] = useState('importar');
  const [importando, setImportando] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [ultimoImport, setUltimoImport] = useState(null);
  const [importaciones, setImportaciones] = useState([]);

  const loadMeta = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await api.getClientesImportaciones();
      const rows = res.data || [];
      setImportaciones(rows);
      if (rows.length) setUltimoImport(rows[0]);
    } catch (err) {
      setImportaciones([]);
      setLoadError(err.message || 'No se pudo cargar el historial');
      onToast?.(err.message || 'No se pudo cargar el historial', 'error');
    } finally {
      setLoading(false);
    }
  }, [pin, onToast]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useInvalidate(['client'], loadMeta);

  async function leerTxt(file) {
    const content = await file.text();
    if (!String(content || '').trim()) {
      throw new Error('El archivo está vacío');
    }
    return content;
  }

  async function importarTercero(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (esSucursalArchivo(file.name)) {
      onToast?.('Ese archivo es de sucursales. Usa el botón Subir sucursal.txt.', 'error');
      e.target.value = '';
      return;
    }
    setImportando('tercero');
    try {
      const content = await leerTxt(file);
      const res = await api.importarClientesTxt({ content, archivo: file.name });
      const s = res.data || {};
      setUltimoImport(s);
      onToast?.(
        `Clientes: ${s.filas || 0} del TXT · ${s.insertados || 0} nuevos · ${s.actualizados || 0} actualizados · ${s.desactivados || 0} ocultos`
      );
      loadMeta();
      setTab('historial');
    } catch (err) {
      onToast?.(err.message || 'No se pudo importar el TXT', 'error');
    } finally {
      setImportando('');
      e.target.value = '';
    }
  }

  async function importarSucursal(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (esTerceroArchivo(file.name)) {
      onToast?.('Ese archivo es de clientes. Usa el botón Subir tercero.txt.', 'error');
      e.target.value = '';
      return;
    }
    setImportando('sucursal');
    try {
      const content = await leerTxt(file);
      const res = await api.importarSucursalesTxt({ content, archivo: file.name });
      const s = res.data || {};
      setUltimoImport(s);
      onToast?.(
        `Sucursales: ${s.filas || 0} del TXT · ${s.sedes_insertadas || 0} sedes nuevas · ${s.sedes_actualizadas || 0} actualizadas · ${s.padres_creados || 0} clientes creados`
      );
      loadMeta();
      setTab('historial');
    } catch (err) {
      onToast?.(err.message || 'No se pudo importar sucursal.txt', 'error');
    } finally {
      setImportando('');
      e.target.value = '';
    }
  }

  const busy = Boolean(importando);

  return (
    <div className="panel clientes-import-page">
      <header className="panel-head">
        <div>
          <h2>Importar clientes</h2>
        </div>
      </header>

      <div className="tabs">
        <button
          type="button"
          className={tab === 'importar' ? 'active' : ''}
          onClick={() => setTab('importar')}
        >
          Importar
        </button>
        <button
          type="button"
          className={tab === 'historial' ? 'active' : ''}
          onClick={() => setTab('historial')}
        >
          Historial
        </button>
      </div>

      {loadError ? (
        <p className="error banner">
          {loadError}{' '}
          <button type="button" className="link-btn" onClick={loadMeta}>
            Reintentar
          </button>
        </p>
      ) : null}

      {tab === 'importar' ? (
        <div className="inventario-import">
          <p className="import-hint">
            Son dos archivos distintos. Ninguno toca productos ni fotos. Las sedes manuales no se apagan.
          </p>

          <div className="import-cards">
            <article className="import-card">
              <strong>Clientes</strong>
              <span>tercero.txt</span>
              <p>El listado de clientes del ERP. Actualiza o crea el padre.</p>
              <input
                ref={terceroRef}
                type="file"
                accept=".txt,text/plain,.TXT"
                disabled={busy}
                onChange={importarTercero}
                style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}
                tabIndex={-1}
                aria-hidden="true"
              />
              <button
                type="button"
                className="btn-primary file-btn"
                disabled={busy}
                onClick={() => terceroRef.current?.click()}
              >
                {importando === 'tercero' ? 'Importando…' : 'Subir tercero.txt'}
              </button>
            </article>

            <article className="import-card">
              <strong>Sucursales / sedes</strong>
              <span>sucursal.txt</span>
              <p>Las deja debajo del cliente padre (mismo NIT). No crea filas sueltas.</p>
              <input
                ref={sucursalRef}
                type="file"
                accept=".txt,text/plain,.TXT"
                disabled={busy}
                onChange={importarSucursal}
                style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}
                tabIndex={-1}
                aria-hidden="true"
              />
              <button
                type="button"
                className="btn-primary file-btn"
                disabled={busy}
                onClick={() => sucursalRef.current?.click()}
              >
                {importando === 'sucursal' ? 'Importando…' : 'Subir sucursal.txt'}
              </button>
            </article>
          </div>

          <div className="import-actions">
            <button type="button" className="btn-ghost" disabled={loading || busy} onClick={loadMeta}>
              Actualizar
            </button>
          </div>

          {ultimoImport ? (
            <div className="import-result">
              <strong>Última importación</strong>
              <span>Archivo: {ultimoImport.archivo || '—'}</span>
              <span>Filas: {ultimoImport.filas ?? '—'}</span>
              <span>Nuevos: {ultimoImport.insertados ?? 0}</span>
              <span>Actualizados: {ultimoImport.actualizados ?? 0}</span>
              {ultimoImport.tipo === 'sucursal' || esSucursalArchivo(ultimoImport.archivo) ? (
                <span>Sedes nuevas: {ultimoImport.sedes_insertadas ?? ultimoImport.insertados ?? 0}</span>
              ) : (
                <span>Ocultos: {ultimoImport.desactivados ?? 0}</span>
              )}
            </div>
          ) : (
            <p className="muted">Aún no hay importaciones.</p>
          )}
        </div>
      ) : null}

      {tab === 'historial' ? (
        <div className="table-wrap">
          {loading ? <p className="muted">Cargando…</p> : null}
          {!loading && !importaciones.length ? (
            <p className="muted">Todavía no hay historial de importaciones.</p>
          ) : null}
          {importaciones.length ? (
            <table className="responsive-cards">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Archivo</th>
                  <th>Filas</th>
                  <th>Nuevos</th>
                  <th>Actualizados</th>
                  <th>Ocultos</th>
                  <th>Omitidos</th>
                </tr>
              </thead>
              <tbody>
                {importaciones.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Fecha">{formatFecha(row.created_at)}</td>
                    <td data-label="Archivo">{row.archivo || '—'}</td>
                    <td data-label="Filas">{row.filas ?? 0}</td>
                    <td data-label="Nuevos">{row.insertados ?? 0}</td>
                    <td data-label="Actualizados">{row.actualizados ?? 0}</td>
                    <td data-label="Ocultos">{row.desactivados ?? 0}</td>
                    <td data-label="Omitidos">{row.omitidos ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
